import { CurrencyPipe, DatePipe, Location, NgClass } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { CanDirective, CapabilitiesService, CapabilityNames } from 'core';
import { LoadingSpinner, TableColumnInfo, TableView, TypeOfColumn } from 'design-system';
import { finalize, switchMap, of } from 'rxjs';
import { receiptsDepositSlipActionCell } from '../../components/receipts-deposit-slip-action-cell/receipts-deposit-slip-action-cell';
import { receiptsCheckVerificationCell } from '../../components/receipts-check-verification-cell/receipts-check-verification-cell';
import {
  DepositCashDialogData,
  receiptsDepositCashDialogComponent,
} from '../../components/dialog-components/receipts-deposit-cash-dialog-component/receipts-deposit-cash-dialog-component';
import {
  DepositReceiptDialogData,
  receiptsDepositReceiptDialogComponent,
} from '../../components/dialog-components/receipts-deposit-receipt-dialog-component/receipts-deposit-receipt-dialog-component';
import { DepositDetail, DepositStatus, UiOption } from '../../interfaces/deposit.model';
import { DepositsService } from '../../services/deposits.service';
import { downloadBlob, formatDateLabel, toDate } from '../../utils/receipt.utils';
import {
  ConfirmDialogData,
  receiptsConfirmDialogComponent,
} from '../../components/dialog-components/receipts-confirm-dialog-component/receipts-confirm-dialog-component';

interface SlipDetail {
  numeroDistinta: number;
  situazione: string;
  dataCreazione: Date | null;
  versamentiContanti: {
    idIncasso: number;
    dataIncasso: Date | null;
    totaleMonete: number;
    totaleBanconote: number;
    importoTotale: number;
    tagli: { taglio: number; quantita: number; importo: number }[];
  }[];
  assegni: {
    id: string;
    idIncasso: number;
    dataIncasso: Date | null;
    abi: string;
    cab: string;
    banca: string;
    numeroAssegno: string;
    importoTotale: number;
    verificato: boolean;
  }[];
  riepilogo: {
    totaleContanti: number;
    totaleAssegni: number;
    totaleDistinta: number;
  };
}

const STATUS_LABELS: Record<string, string> = {
  [DepositStatus.Lavorazione]: 'Lavorazione',
  [DepositStatus.Verificata]: 'Verificata',
  [DepositStatus.Chiusa]: 'Chiusa',
};

const EMPTY_DETAIL: SlipDetail = {
  numeroDistinta: 0,
  situazione: '',
  dataCreazione: null,
  versamentiContanti: [],
  assegni: [],
  riepilogo: { totaleContanti: 0, totaleAssegni: 0, totaleDistinta: 0 },
};

@Component({
  selector: 'rev-receipts-distinte-detail',
  imports: [
    CanDirective,
    CurrencyPipe,
    DatePipe,
    FormsModule,
    LoadingSpinner,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatTabsModule,
    NgClass,
    TableView,
  ],
  templateUrl: './receipts-distinte-detail.html',
  styleUrl: './receipts-distinte-detail.scss',
})
export class ReceiptsDistinteDetail implements OnInit {
  private static readonly MAX_RECEIPT_FILE_SIZE = 10 * 1024 * 1024;
  private static readonly ALLOWED_RECEIPT_FILE_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
  ];

  private readonly service = inject(DepositsService);
  private readonly route = inject(ActivatedRoute);
  private readonly snackBar = inject(MatSnackBar);
  private readonly location = inject(Location);
  private readonly capabilities = inject(CapabilitiesService);

  readonly CAPS = CapabilityNames;

  constructor(private readonly dialog: MatDialog) {}

  idDistinta = '';
  isLoading = false;
  isSaving = false;
  selectedTabIndex = 0;

  dettaglio: SlipDetail = EMPTY_DETAIL;
  private stato: DepositStatus | null = null;

  banche: UiOption[] = [];
  codBanca = '';
  numeroOperazione = '';
  dataVersamento: Date | null = new Date();

  ricevutaFile: File | null = null;
  ricevutaFileNonValido = false;

  versamentiContantiTable: Record<string, unknown>[] = [];
  assegniTable: (Omit<SlipDetail['assegni'][number], 'dataIncasso'>
    & {
      dataIncasso: string;
      selezionato: boolean;
      selezionaAssegno: (checked: boolean) => void;
    })[] = [];
  selectedAssegnoIds = new Set<string>();

  // Comandi che competono al profilo ma non sempre allo stato: si
  // disabilitano, non si nascondono.
  get puoVerificare(): boolean {
    return this.capabilities.can(CapabilityNames.distintaVerifica);
  }

  get puoChiudereDistinta(): boolean {
    return this.capabilities.can(CapabilityNames.distintaChiudi);
  }

  get inLavorazione(): boolean {
    return this.stato === DepositStatus.Lavorazione;
  }

  get verificata(): boolean {
    return this.stato === DepositStatus.Verificata;
  }

  statusBadgeClass(status: string): string {
    switch (status) {
      case STATUS_LABELS[DepositStatus.Lavorazione]:
        return 'deposit-status-warning';
      case STATUS_LABELS[DepositStatus.Verificata]:
        return 'deposit-status-success';
      case STATUS_LABELS[DepositStatus.Chiusa]:
        return 'deposit-status-info';
      default:
        return '';
    }
  }

  // I dati del versamento si compilano solo a distinta Verificata: prima non
  // c'e' niente da versare, dopo la chiusura sono storia.
  get versamentoReadOnly(): boolean {
    return !this.verificata;
  }

  get assegniDaVerificare(): boolean {
    return this.assegniTable.some(assegno => !assegno.verificato);
  }

  get haAssegniSelezionati(): boolean {
    return this.selectedAssegnoIds.size > 0;
  }

  get tuttiAssegniVerificati(): boolean {
    return this.haVersamentiAssegni
      && (this.dettaglio.assegni.length === 0
        || this.dettaglio.assegni.every(assegno => assegno.verificato));
  }

  get haVersamentiContanti(): boolean {
    return this.dettaglio.versamentiContanti.length > 0
      || this.dettaglio.riepilogo.totaleContanti > 0;
  }

  get haVersamentiAssegni(): boolean {
    return this.dettaglio.assegni.length > 0
      || this.dettaglio.riepilogo.totaleAssegni > 0;
  }

  get puoChiudere(): boolean {
    return this.puoChiudereDistinta
      && this.verificata
      && !!this.codBanca
      && !!this.numeroOperazione.trim()
      && !!this.dataVersamento;
  }

  readonly versamentiContantiColumns: TableColumnInfo[] = [
    { columnNameDef: 'idIncasso', label: 'Incasso', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataIncasso', label: 'Data incasso', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'totaleMonete', label: 'Totale monete', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'totaleBanconote', label: 'Totale banconote', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'importoTotale', label: 'Importo totale', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'azioni', label: 'Azioni', type: TypeOfColumn.COMPONENT,
      component: receiptsDepositSlipActionCell,
    },
  ];

  readonly assegniVerificaColumns: TableColumnInfo[] = [
    { columnNameDef: 'idIncasso', label: 'Incasso', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataIncasso', label: 'Data incasso', type: TypeOfColumn.TEXT },
    { columnNameDef: 'abi', label: 'ABI', type: TypeOfColumn.TEXT },
    { columnNameDef: 'cab', label: 'CAB', type: TypeOfColumn.TEXT },
    { columnNameDef: 'banca', label: 'Banca', type: TypeOfColumn.TEXT },
    { columnNameDef: 'numeroAssegno', label: 'N. Assegno', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'importoTotale', label: 'Importo totale', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'verifica', label: 'Verifica', type: TypeOfColumn.COMPONENT,
      component: receiptsCheckVerificationCell,
    },
  ];

  ngOnInit(): void {
    this.service.getBancheVersamento().subscribe({
      next: banche => (this.banche = banche ?? []),
      error: err => console.error('Errore caricamento banche di versamento', err),
    });

    this.route.paramMap.subscribe(params => {
      const routeId = params.get('idDistinta');

      if (!routeId) {
        console.error('ID distinta mancante in URL');
        return;
      }

      this.idDistinta = routeId;
      this.loadDetail();
    });
  }

  loadDetail(): void {
    this.isLoading = true;

    this.service
      .getDepositDetail(this.idDistinta)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: detail => {
          if (!detail) {
            this.snackBar.open('Distinta non trovata.', 'Chiudi', { duration: 5000 });
            this.tornaIndietro();
            return;
          }

          this.stato = detail.stato;
          this.dettaglio = this.toDetail(detail);
          this.versamentiContantiTable = this.dettaglio.versamentiContanti.map(versamento => ({
            ...versamento,
            dataIncasso: formatDateLabel(versamento.dataIncasso),
            apriDettaglio: () => this.apriDettaglioContanti(versamento.idIncasso),
          }));
          this.selectedAssegnoIds.clear();
          this.assegniTable = this.dettaglio.assegni.map(assegno => ({
            ...assegno,
            dataIncasso: formatDateLabel(assegno.dataIncasso),
            selezionato: false,
            selezionaAssegno: (checked: boolean) => this.selezionaAssegno(assegno.id, checked),
          }));

          this.codBanca = detail.codBanca ?? this.codBanca;
          this.numeroOperazione = detail.numeroOperazione ?? this.numeroOperazione;
          this.dataVersamento = detail.dataOperazione
            ? new Date(detail.dataOperazione)
            : this.dataVersamento;
        },
        error: err => {
          console.error('Errore caricamento distinta', err);
          this.snackBar.open('Caricamento della distinta non riuscito.', 'Chiudi', {
            duration: 5000,
          });
        },
      });
  }

  verificaAssegni(): void {
    const idChecks = [...this.selectedAssegnoIds];

    if (idChecks.length === 0 || this.isSaving || !this.puoVerificare) {
      return;
    }

    this.isSaving = true;

    this.service
      .verifyChecks(this.idDistinta, idChecks)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => this.loadDetail(),
        error: err => {
          console.error('Errore verifica assegni', err);
          this.snackBar.open('Verifica degli assegni non riuscita.', 'Chiudi', { duration: 5000 });
        },
      });
  }

  isAssegnoSelezionato(idAssegno: string): boolean {
    return this.selectedAssegnoIds.has(idAssegno);
  }

  selezionaAssegno(idAssegno: string, checked: boolean): void {
    if (checked) {
      this.selectedAssegnoIds.add(idAssegno);
    } else {
      this.selectedAssegnoIds.delete(idAssegno);
    }
  }

  verificaDistinta(): void {
    if (!this.inLavorazione || this.isSaving || !this.puoVerificare) {
      return;
    }

    this.isSaving = true;

    this.service
      .verifyDeposit(this.idDistinta)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => this.loadDetail(),
        error: err => {
          console.error('Errore verifica distinta', err);
          this.snackBar.open(
            this.assegniDaVerificare
              ? 'Ci sono ancora assegni da verificare.'
              : 'Verifica della distinta non riuscita.',
            'Chiudi',
            { duration: 5000 },
          );
        },
      });
  }

  // L'allegato va caricato prima: la chiusura e' irreversibile.
  caricaEChiudi(): void {
    if (!this.puoChiudere || this.isSaving) {
      return;
    }

    const file = this.ricevutaFile;
    this.isSaving = true;

    const upload$ = file ? this.service.uploadReceipt(this.idDistinta, file) : of(void 0);

    upload$
      .pipe(
        switchMap(() =>
          this.service.closeDeposit(this.idDistinta, {
            codBanca: this.codBanca,
            numeroOperazione: this.numeroOperazione.trim(),
            dataOperazione: this.dataVersamento!.toISOString(),
          }),
        ),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: () => {
          this.apriRicevutaVersamento();
          this.loadDetail();
        },
        error: err => {
          console.error('Errore chiusura distinta', err);
          this.snackBar.open(
            'Chiusura non riuscita. La distinta non e stata chiusa.',
            'Chiudi',
            { duration: 5000 },
          );
        },
      });
  }

  apriDettaglioContanti(idIncasso: number): void {
    const versamento = this.dettaglio.versamentiContanti.find(v => v.idIncasso === idIncasso);

    if (!versamento) {
      return;
    }

    const data: DepositCashDialogData = {
      idIncasso: versamento.idIncasso,
      dataIncasso: formatDateLabel(versamento.dataIncasso),
      totaleMonete: versamento.totaleMonete,
      totaleBanconote: versamento.totaleBanconote,
      totale: versamento.importoTotale,
      tagli: versamento.tagli,
    };

    this.dialog.open(receiptsDepositCashDialogComponent, { data });
  }

  get puoEliminare(): boolean {
    return this.inLavorazione && this.capabilities.can(CapabilityNames.distintaElimina);
  }

  elimina(): void {
    if (!this.puoEliminare || this.isSaving) {
      return;
    }

    const data: ConfirmDialogData = { message: 'Vuoi eliminare la distinta corrente?' };

    this.dialog
      .open<receiptsConfirmDialogComponent, ConfirmDialogData, boolean>(
        receiptsConfirmDialogComponent,
        { data },
      )
      .afterClosed()
      .subscribe(confermato => {
        if (confermato) {
          this.eseguiEliminazione();
        }
      });
  }

  private eseguiEliminazione(): void {
    this.isSaving = true;

    this.service
      .deleteDeposit(this.idDistinta)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => this.tornaIndietro(),
        error: err => {
          console.error('Errore eliminazione distinta', err);
          this.snackBar.open('Eliminazione non riuscita.', 'Chiudi', { duration: 5000 });
        },
      });
  }

  scaricaPdf(): void {
    this.service.downloadDepositPdf(this.idDistinta).subscribe({
      next: blob => downloadBlob(blob, `distinta-versamenti-${this.dettaglio.numeroDistinta}.pdf`),
      error: err => {
        console.error('Errore download distinta', err);
        this.snackBar.open('Stampa non disponibile.', 'Chiudi', { duration: 5000 });
      },
    });
  }

  apriSelettoreFile(fileInput: HTMLInputElement): void {
    if (this.versamentoReadOnly) {
      return;
    }

    fileInput.click();
  }

  apriRicevutaVersamento(): void {
    const data: DepositReceiptDialogData = {
      numeroDistinta: String(this.dettaglio.numeroDistinta),
      dataConferma: this.dataVersamento ? this.dataVersamento.toISOString() : '',
      totaleContanti: this.dettaglio.riepilogo.totaleContanti,
      totaleAssegni: this.dettaglio.riepilogo.totaleAssegni,
      totaleDistinta: this.dettaglio.riepilogo.totaleDistinta,
      situazione: this.dettaglio.situazione,
    };

    this.dialog.open(receiptsDepositReceiptDialogComponent, { data });
  }

  selezionaRicevuta(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.impostaRicevuta(input.files?.[0] ?? null);
    input.value = '';
  }

  trascinaRicevuta(event: DragEvent): void {
    event.preventDefault();
  }

  rilasciaRicevuta(event: DragEvent): void {
    event.preventDefault();

    if (this.versamentoReadOnly) {
      return;
    }

    this.impostaRicevuta(event.dataTransfer?.files[0] ?? null);
  }

  tornaIndietro(): void {
    this.location.back();
  }

  private impostaRicevuta(file: File | null): void {
    const fileValido = !!file
      && ReceiptsDistinteDetail.ALLOWED_RECEIPT_FILE_TYPES.includes(file.type)
      && file.size <= ReceiptsDistinteDetail.MAX_RECEIPT_FILE_SIZE;

    this.ricevutaFile = fileValido ? file : null;
    this.ricevutaFileNonValido = !!file && !fileValido;
  }

  private toDetail(detail: DepositDetail): SlipDetail {
    const totaleContanti = detail.totaleValuta ?? 0;
    const totaleAssegni = detail.totaleAssegni ?? 0;

    return {
      numeroDistinta: detail.numeroDistintaVersamenti,
      situazione: STATUS_LABELS[detail.stato] ?? detail.stato,
      dataCreazione: toDate(detail.data),
      versamentiContanti: (detail.versamentiContante ?? []).map(versamento => ({
        idIncasso: versamento.numeroIncasso ?? 0,
        dataIncasso: toDate(versamento.dataIncasso),
        totaleMonete: versamento.totaleMonete ?? 0,
        totaleBanconote: versamento.totaleBanconote ?? 0,
        importoTotale: versamento.totale ?? 0,
        tagli: versamento.tagli ?? [],
      })),
      assegni: (detail.assegniDaVerificare ?? []).map(assegno => ({
        id: assegno.id,
        idIncasso: assegno.numeroIncasso,
        dataIncasso: toDate(assegno.dataIncasso),
        abi: assegno.abi,
        cab: assegno.cab,
        banca: assegno.nomeBanca,
        numeroAssegno: assegno.numeroAssegno,
        importoTotale: assegno.importo,
        verificato: assegno.flagAssegnoVerificato,
      })),
      riepilogo: {
        totaleContanti,
        totaleAssegni,
        totaleDistinta: detail.totaleVersamento ?? totaleContanti + totaleAssegni,
      },
    };
  }
}
